/**
 * 課本句型跟讀資料 — 翰林三下英語各單元核心句型(長期路線圖 P3a,boss 2026-06-11
 * 拍板照建議走)。
 *
 * 句子來源鐵則:**全部取材自 repo 既有翰林三下英語題庫的真實句子**
 * (public/data/english-grammar-*-翰林.json、english-dialogue-classroom-翰林.json、
 * english-greetings-翰林.json、english-vocab-*-翰林.json、english-colors-翰林.json、
 * english-numbers-*-翰林.json 的正解選項 / 引述句),不憑空編課文;中文意思同樣
 * 對照題庫 explanation。
 *
 * 「課」的劃分 = 題庫 KP 主題單元(repo 內沒有逐課課文,題庫即三下內容的單一真相);
 * `kps` 欄位記錄每課對應的題庫 KP,為 P3c 發音弱點 → 題庫回流鋪路。
 *
 * 學習/遊戲分離(CLAUDE.md 紅線):純學習側資料,不碰任何戰鬥數值 / commanderXp。
 */

/** 跟讀一句:示範句 + 中文意思。 */
export interface ShadowingSentence {
  /** 穩定 id(音檔檔名 / localStorage 追蹤依賴),一律 `sh-` 前綴避免撞口試題 id。 */
  id: string;
  /** 英文目標句 — TTS 示範與發音比對的目標。 */
  en: string;
  /** 中文意思。 */
  zh: string;
}

/** 跟讀一課(對應題庫 KP 主題單元)。 */
export interface ShadowingLesson {
  id: string;
  /** 顯示標題(中性、無學科名以外的戲謔)。 */
  title: string;
  /** 來源題庫 KP(資料層;為 P3c 弱點回流鋪路)。 */
  kps: string[];
  sentences: ShadowingSentence[];
}

/** 翰林三下句型跟讀 — 8 課,每課 4-8 句。 */
export const SHADOWING_LESSONS: ShadowingLesson[] = [
  {
    id: 'sh-l1',
    title: '第 1 課 日常問候',
    kps: ['english.dialogue.daily'],
    sentences: [
      { id: 'sh-l1-1', en: 'Good morning!', zh: '早安!' },
      { id: 'sh-l1-2', en: 'Good afternoon!', zh: '午安!' },
      { id: 'sh-l1-3', en: 'Good night!', zh: '晚安!(睡前說)' },
      { id: 'sh-l1-4', en: 'I am fine, thank you.', zh: '我很好,謝謝。' },
      { id: 'sh-l1-5', en: 'My name is Amy.', zh: '我叫艾美。' },
      { id: 'sh-l1-6', en: 'Nice to meet you, too!', zh: '我也很高興認識你!' },
      { id: 'sh-l1-7', en: "You're welcome.", zh: '不客氣。' },
      { id: 'sh-l1-8', en: 'See you!', zh: '再見!(待會見)' },
    ],
  },
  {
    id: 'sh-l2',
    title: '第 2 課 教室用語',
    kps: ['english.dialogue.classroom'],
    sentences: [
      { id: 'sh-l2-1', en: 'Good morning, teacher.', zh: '老師早安。' },
      { id: 'sh-l2-2', en: 'Open your book.', zh: '打開你的書。' },
      { id: 'sh-l2-3', en: 'Close your book.', zh: '把書闔上。' },
      { id: 'sh-l2-4', en: 'Stand up, please.', zh: '請起立。' },
      { id: 'sh-l2-5', en: 'Sit down, please.', zh: '請坐下。' },
      { id: 'sh-l2-6', en: 'May I go to the bathroom?', zh: '我可以去洗手間嗎?' },
      { id: 'sh-l2-7', en: 'I don\'t know.', zh: '我不知道。' },
      { id: 'sh-l2-8', en: 'Class is over.', zh: '下課了。' },
    ],
  },
  {
    id: 'sh-l3',
    title: '第 3 課 介紹自己與別人',
    kps: ['english.grammar.statements'],
    sentences: [
      { id: 'sh-l3-1', en: 'I am a student.', zh: '我是學生。' },
      { id: 'sh-l3-2', en: 'He is a doctor.', zh: '他是醫生。' },
      { id: 'sh-l3-3', en: 'We are good friends.', zh: '我們是好朋友。' },
      { id: 'sh-l3-4', en: 'You are nine years old.', zh: '你九歲了。' },
      { id: 'sh-l3-5', en: 'The dogs are cute.', zh: '那些狗很可愛。' },
      { id: 'sh-l3-6', en: "She isn't a teacher.", zh: '她不是老師。' },
      { id: 'sh-l3-7', en: "They aren't happy.", zh: '他們不快樂。' },
      { id: 'sh-l3-8', en: 'This book is interesting.', zh: '這本書很有趣。' },
    ],
  },
  {
    id: 'sh-l4',
    title: '第 4 課 問句與回答',
    kps: ['english.grammar.questions'],
    sentences: [
      { id: 'sh-l4-1', en: 'Are you a student?', zh: '你是學生嗎?' },
      { id: 'sh-l4-2', en: 'Yes, I am.', zh: '是的,我是。' },
      { id: 'sh-l4-3', en: 'Is he a teacher?', zh: '他是老師嗎?' },
      { id: 'sh-l4-4', en: "No, she isn't.", zh: '不,她不是。' },
      { id: 'sh-l4-5', en: 'Is this your bag?', zh: '這是你的書包嗎?' },
      { id: 'sh-l4-6', en: 'Are they your friends?', zh: '他們是你的朋友嗎?' },
      { id: 'sh-l4-7', en: 'What is this?', zh: '這是什麼?' },
      { id: 'sh-l4-8', en: 'Am I late?', zh: '我遲到了嗎?' },
    ],
  },
  {
    id: 'sh-l5',
    title: '第 5 課 我的家人',
    kps: ['english.vocab.family'],
    sentences: [
      { id: 'sh-l5-1', en: 'This is my mother.', zh: '這是我媽媽。' },
      { id: 'sh-l5-2', en: "He's my father.", zh: '他是我爸爸。' },
      { id: 'sh-l5-3', en: 'She is my sister.', zh: '她是我姊姊。' },
      { id: 'sh-l5-4', en: "Who's she?", zh: '她是誰?' },
      { id: 'sh-l5-5', en: "No, he isn't.", zh: '不,他不是。' },
    ],
  },
  {
    id: 'sh-l6',
    title: '第 6 課 動物',
    kps: ['english.vocab.animals'],
    sentences: [
      { id: 'sh-l6-1', en: "It's a cat.", zh: '牠是一隻貓。' },
      { id: 'sh-l6-2', en: 'That is a dog.', zh: '那是一隻狗。' },
      { id: 'sh-l6-3', en: 'It is a fish.', zh: '牠是一條魚。' },
      { id: 'sh-l6-4', en: 'It is a big elephant.', zh: '牠是一隻大象。' },
      { id: 'sh-l6-5', en: 'I have a cat.', zh: '我有一隻貓。' },
      { id: 'sh-l6-6', en: 'I have three cats.', zh: '我有三隻貓。' },
    ],
  },
  {
    id: 'sh-l7',
    title: '第 7 課 食物與點心',
    kps: ['english.vocab.food'],
    sentences: [
      { id: 'sh-l7-1', en: 'I like apples.', zh: '我喜歡蘋果。' },
      { id: 'sh-l7-2', en: "I'm thirsty.", zh: '我口渴了。' },
      { id: 'sh-l7-3', en: 'Do you want some cake?', zh: '你想要一些蛋糕嗎?' },
      { id: 'sh-l7-4', en: "No, I don't.", zh: '不,我不要。' },
      { id: 'sh-l7-5', en: 'Apple juice, please.', zh: '請給我蘋果汁。' },
      { id: 'sh-l7-6', en: 'Mom is baking a cake.', zh: '媽媽正在烤蛋糕。' },
    ],
  },
  {
    id: 'sh-l8',
    title: '第 8 課 學校、顏色與數字',
    kps: ['english.vocab.school', 'english.vocab.colors', 'english.numbers.basic', 'english.numbers.ordinal'],
    sentences: [
      { id: 'sh-l8-1', en: 'I have a book.', zh: '我有一本書。' },
      { id: 'sh-l8-2', en: 'It is in my pencil case.', zh: '它在我的鉛筆盒裡。' },
      { id: 'sh-l8-3', en: 'It is blue.', zh: '它是藍色的。' },
      { id: 'sh-l8-4', en: 'The apple is red.', zh: '這顆蘋果是紅色的。' },
      { id: 'sh-l8-5', en: 'I have fifteen candies.', zh: '我有十五顆糖果。' },
      { id: 'sh-l8-6', en: 'Time for lunch!', zh: '午餐時間到了!' },
      { id: 'sh-l8-7', en: 'He is the eighth.', zh: '他是第八個。' },
      { id: 'sh-l8-8', en: 'Today is her sixth birthday.', zh: '今天是她六歲生日。' },
    ],
  },
];

/** 一句對應的離線示範音檔路徑(public/ 之下,Vite 以根目錄提供)。 */
export function shadowingAudioUrl(sentence: ShadowingSentence): string {
  return `/audio/oral/${sentence.id}.mp3`;
}
